/**
 * Executors for the agent tools — one code path for the built-in chat agent and for an
 * external agent over the MCP bridge. Every mutating tool runs as one undo step, reports in
 * plain text a model can act on, and never throws past `runTool`.
 */
import type { Item, Level, Opening, Surface, Vec2 } from '../core/hp/types';
import { levelLabel } from '../state/levelTitles';
import { store as liveStore, type DocumentScope, type DocumentConflict, type MaterialTarget, type Selection } from '../state/store';
import { loadAgentCatalog, loadAgentMaterials, modelRefOf, searchCatalog, type Catalog, type CatalogItem } from './agentCatalog';
import type { MaterialEntry } from '../state/materials';
import { interiorPoint, placementPoint } from '../state/placement';
import { wallItemPlacement } from '../state/catalogPlacement';
import { constrainItemToRoom } from '../state/sceneGesture';
import { transformItemWithStack } from '../core/geometry/stacking';
import { obstructions } from '../core/geometry/overlap';
import { itemFootprint } from '../core/geometry/footprint';
import { canvasPreviewBlob } from '../state/projectPreview';
import { localProjects } from '../state/projectLibrary';
import { demoProjects } from '../ui/home/DemoProjects';
import { appAccess, editorAccess, type AppAccess, type EditorAccess } from './editorAccess';
import { describeItem, describeScene, fmt, frontOf, roomOfItem, roomOfPoint, toDegrees, toRadians, type Namer } from './sceneSummary';
import { FURNISH_PRESETS, TOOL_DEFINITIONS, toolDefinition, toolEffect } from './toolSchemas';
import type { OpeningKind } from '../state/planConstruction';
import { expandQuery, usableCatalogItems } from './catalogFilter';
import { agentCursor } from './cursor';
import { aliases as aliasRegistry, aliasScope, type ScopedAliases } from './aliases';
import { inwardNormal, roomWalls, SIDE_FROM_INPUT, SIDE_LETTER, wallBySide, wallSide, type WallSide } from './layout';
import { planAround, planBeside, planPreset, planRun, PRESET_FOR_ROOM_TYPE, type Planned, type Preset } from './furnish';
import type { Room, Wall } from '../core/hp/types';
import type { CommitDecision, Json } from '../collaboration/protocol';
import { plural, t, type TranslationKey } from '../i18n/I18n';
import { ROLE_IDS, roleById } from './roles';

export type ToolContent = { type: 'text'; text: string } | { type: 'image'; mediaType: 'image/jpeg' | 'image/png'; data: string };
export interface CommandMetadata { commandId: string; callerId: string; scope?: DocumentScope; turnId?: string }
export interface CommandOutcome {
  commandId: string;
  status: 'committed' | 'unchanged' | 'rejected' | 'cancelled' | 'pending';
  scope: DocumentScope | null;
  baseRevision: number;
  revision: number;
  acceptedRevision?: number;
  /** `outside_rights`: the agent's own rights refused it; `forbidden`: the shared project did. */
  code?: DocumentConflict | 'duplicate_mismatch' | 'forbidden' | 'outside_rights' | 'invalid_command' | 'tool_error' | 'conflict' | 'invalid';
}
export interface ToolOutcome {
  content: ToolContent[];
  isError?: boolean;
  /** One line for the chat card. */
  summary: string;
  /** Where on the plan the work landed, for the agent's cursor. */
  anchor?: Vec2 | null;
  command?: CommandOutcome;
}

export interface RenderRequestInput { type: 'photo' | 'redesign'; style?: string; prompt?: string; quality?: 'HD' | 'FullHD' }
export interface RenderOutcome { imageUrl: string; galleryId: string; elapsedMs: number }

/** What a host lends the tools; everything is optional so tests and the bridge can run without a browser. */
export interface ToolServices {
  command?: CommandMetadata;
  allowedTools?: readonly string[];
  catalog?: () => Promise<Catalog>;
  materials?: () => Promise<MaterialEntry[]>;
  editor?: () => EditorAccess | null;
  app?: () => AppAccess | null;
  delegate?: (agentId: string, task: string) => Promise<string>;
  render?: (input: RenderRequestInput, signal?: AbortSignal) => Promise<RenderOutcome>;
  signal?: AbortSignal;
  /** Whose cursor the calls move — a roster role id, or the name of an agent outside the roster: subagents working side by side each show their own. */
  agent?: string;
  /** What the calling subagent may change: a reason when the draft goes past it, and the draft is then dropped. */
  rights?: (before: Level, after: Level) => string | null;
}

class ToolError extends Error {}
type Input = Record<string, unknown>;

const text = (value: string): ToolContent => ({ type: 'text', text: value });
const done = (summary: string, detail = summary, anchor?: Vec2 | null): ToolOutcome => ({ content: [text(detail)], summary, ...(anchor ? { anchor } : {}) });

function number(input: Input, key: string): number | undefined {
  const value = input[key];
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) throw new ToolError(`"${key}" must be a number, got ${JSON.stringify(value)}.`);
  return parsed;
}
function integer(input: Input, key: string): number | undefined {
  const value = number(input, key);
  if (value !== undefined && !Number.isInteger(value)) throw new ToolError(`"${key}" must be an integer.`);
  return value;
}
function string(input: Input, key: string): string | undefined {
  const value = input[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') throw new ToolError(`"${key}" must be a string.`);
  return value.trim() || undefined;
}
function required<T>(value: T | undefined, key: string): T {
  if (value === undefined) throw new ToolError(`"${key}" is required.`);
  return value;
}
function choice<const T extends string>(input: Input, key: string, values: readonly T[]): T | undefined {
  const value = string(input, key);
  if (value === undefined) return undefined;
  if (!values.includes(value as T)) throw new ToolError(`"${key}" must be one of ${values.join(', ')}.`);
  return value as T;
}
function flag(input: Input, key: string): boolean | undefined {
  const value = input[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 'false') return value === 'true';
  throw new ToolError(`"${key}" must be true or false.`);
}
const point = (p: Vec2) => `(${fmt(p.x)}, ${fmt(p.z)})`;
const sizeOf = (item: Pick<Item, 'size'>) => `${fmt(item.size.w)}×${fmt(item.size.d)}×${fmt(item.size.h)} m`;

/** What the user sees right now — the plan canvas or the 3D frame — as a JPEG. */
export async function captureViewImage(editor: EditorAccess, maxSide: number): Promise<{ blob: Blob; label: string } | null> {
  const state = liveStore.get();
  if (state.mode === 'plan') {
    const canvas = editor.plan()?.canvasElement();
    const blob = canvas ? await canvasPreviewBlob(canvas, maxSide, '#ededeb', .85) : null;
    return blob ? { blob, label: `2D plan (${state.planSurface})` } : null;
  }
  const blob = (await editor.scene3d()?.snapshot(maxSide)) ?? null;
  return blob ? { blob, label: state.firstPerson ? '3D walk view' : '3D orbit view' } : null;
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
}

// i18n-ignore-start: search words in either language, matched against what a person or a model asks for
/** A material word and its counterparts in the other language and in the library's own spelling. */
const MATERIAL_SYNONYMS: readonly string[][] = [
  ['дуб', 'oak'], ['орех', 'walnut'], ['ясень', 'ash'], ['сосна', 'pine'], ['бук', 'beech'], ['берёза', 'береза', 'birch'], ['клён', 'клен', 'maple'], ['тик', 'teak'],
  ['мрамор', 'marble'], ['гранит', 'granite'], ['плитка', 'плитк', 'tile', 'ceramic'], ['кирпич', 'brick'], ['бетон', 'concrete', 'cement'], ['камень', 'stone'],
  ['паркет', 'parquet'], ['ламинат', 'laminate'], ['доска', 'plank', 'board'], ['ковёр', 'ковер', 'carpet', 'rug'], ['обои', 'wallpaper'], ['штукатурка', 'plaster', 'stucco'],
  ['краска', 'paint'], ['панель', 'panel'], ['дерево', 'дерев', 'wood', 'timber'], ['светлый', 'светл', 'light', 'pale'], ['тёмный', 'темный', 'темн', 'dark'],
  ['белый', 'бел', 'white'], ['серый', 'сер', 'grey', 'gray'], ['чёрный', 'черный', 'black'], ['бежевый', 'beige'], ['коричневый', 'brown'],
];
/** Shades and colours: they narrow a search but never say what the material is. */
const SHADE_WORDS = new Set(['светлый', 'светл', 'light', 'pale', 'тёмный', 'темный', 'темн', 'dark', 'белый', 'бел', 'white', 'серый', 'сер', 'grey', 'gray', 'чёрный', 'черный', 'black', 'бежевый', 'beige', 'коричневый', 'brown']);
// i18n-ignore-end
/** Which collections a material word points at, so "орех" finds the wood walnut before the marble one. */
const COLLECTION_HINTS: ReadonlyArray<[RegExp, string[]]> = [
  [/^(oak|walnut|ash|pine|beech|birch|maple|teak|parquet|laminate|plank|board|wood|timber)$/, ['Wood']], [/^marble$/, ['Marble']], [/^granite$/, ['Granite']],
  [/^(tile|ceramic)$/, ['Tile']], [/^brick$/, ['Bricks']], [/^stone$/, ['Stone']], [/^(carpet|rug)$/, ['Carpet']], [/^wallpaper$/, ['Wallpapers', 'Photo Wallpaper']],
  [/^(plaster|stucco|paint|concrete|cement)$/, ['GeneralFinishes', 'ArtFinishes']], [/^panel$/, ['Panels']],
];
function materialTerms(word: string): string[] {
  const lower = word.toLowerCase();
  const terms = new Set([lower]);
  // «бежевые» and «бежевый» share a stem; a row matches on the word, its prefix, or the first five letters.
  const same = (entry: string) => lower === entry || lower.startsWith(entry) && entry.length >= 3 || lower.length >= 5 && entry.length >= 5 && lower.slice(0, 5) === entry.slice(0, 5);
  for (const row of MATERIAL_SYNONYMS) if (row.some(same)) row.forEach(entry => terms.add(entry));
  return [...terms];
}
/** Collections the query's material words point at (none when the words are only shades). */
function hintedCollections(wordTerms: string[][]): Set<string> {
  const hinted = new Set<string>();
  for (const terms of wordTerms) for (const term of terms) for (const [pattern, collections] of COLLECTION_HINTS) if (pattern.test(term)) collections.forEach(c => hinted.add(c));
  return hinted;
}
/** Library entries for a surface that match the words (either language) — every word first, any word as the fallback. */
export function matchMaterials(library: readonly MaterialEntry[], surface: 'floor' | 'wall' | 'item', query?: string, collection?: string): { pool: MaterialEntry[]; matches: MaterialEntry[] } {
  const pool = library.filter(m => m.surface === surface || (surface === 'item' && ['Wood', 'Marble', 'Tile'].includes(m.collection)));
  const wanted = collection?.toLowerCase();
  const inCollection = pool.filter(m => !wanted || m.collection.toLowerCase() === wanted);
  const haystack = (m: MaterialEntry) => (m.caption + ' ' + m.collection + ' ' + m.texture).toLowerCase();
  const words = (query ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  // «светлый дуб» must find "Oak light": every word, in either language, somewhere in the entry; failing that,
  // the entries matching most words, the material word (parquet, brick) weighing more than the shade (light, beige).
  const wordTerms = words.map(word => materialTerms(word));
  const weight = (terms: string[]) => terms.some(term => SHADE_WORDS.has(term)) ? 1 : 2;
  const score = (m: MaterialEntry) => wordTerms.reduce((sum, terms) => sum + (terms.some(term => haystack(m).includes(term)) ? weight(terms) : 0), 0);
  let matches = words.length ? inCollection.filter(m => wordTerms.every(terms => terms.some(term => haystack(m).includes(term)))) : inCollection;
  if (!matches.length && words.length > 1) matches = inCollection.map(m => ({ m, score: score(m) })).filter(({ score }) => score > 0).sort((a, b) => b.score - a.score).map(({ m }) => m);
  const hinted = hintedCollections(wordTerms);
  if (hinted.size) matches = [...matches].sort((a, b) => Number(hinted.has(b.collection)) - Number(hinted.has(a.collection)));
  return { pool, matches };
}
const textureSurface = (entry: MaterialEntry): Surface => ({ texture: entry.texture, textureKind: 'catalog', tileMeters: entry.tile, shininess: 1 - entry.roughness, caption: entry.caption });

type Executor = (input: Input, services: ToolServices) => Promise<ToolOutcome>;
function createExecutors(store: typeof liveStore, aliases: ScopedAliases): Record<string, Executor> {
  function activeLevel(): Level {
    const level = store.level;
    if (!level) throw new ToolError('No project is open.');
    return level;
  }
  /** The short-name book of the active floor, synced so every object has a name; the namer reads or coins names. */
  function names(): Namer {
    const level = store.level;
    if (level) aliases.sync(level);
    return (kind, id) => aliases.alias(kind, id);
  }
  function findItem(level: Level, ref: string): Item {
    const id = aliases.resolve('item', ref);
    const item = level.items.find(value => value.id === id);
    if (!item) throw new ToolError(`No item "${ref}" on this floor. Items: ${level.items.slice(0, 30).map(value => `${aliases.alias('item', value.id)} ${value.caption ?? ''}`.trim()).join(', ') || 'none'}.`);
    return item;
  }
  function findWall(level: Level, ref: string): Wall {
    const id = aliases.resolve('wall', ref);
    const wall = level.walls.find(value => value.id === id);
    if (!wall) throw new ToolError(`No wall "${ref}" on this floor. Walls: ${level.walls.slice(0, 40).map(value => aliases.alias('wall', value.id)).join(', ') || 'none'}.`);
    return wall;
  }
  function findOpening(level: Level, ref: string): Opening {
    const id = aliases.resolve('opening', ref);
    const opening = level.openings.find(value => value.id === id);
    if (!opening) throw new ToolError(`No opening "${ref}" on this floor. Openings: ${level.openings.map(value => aliases.alias('opening', value.id)).join(', ') || 'none'}.`);
    return opening;
  }
  function parseSide(input: Input, key = 'side'): WallSide | undefined {
    const value = string(input, key);
    if (value === undefined) return undefined;
    const side = SIDE_FROM_INPUT[value.toLowerCase()];
    if (!side) throw new ToolError(`"${key}" must be N, E, S or W, got "${value}".`);
    return side;
  }
  /** The wall a call names — by name/id, or as a side of a room. */
  function resolveWall(level: Level, input: Input): { wall: Wall; room?: Room } {
    const ref = string(input, 'wall') ?? string(input, 'wall_id');
    const roomIndex = integer(input, 'room_index') ?? integer(input, 'room');
    const room = roomIndex !== undefined ? findRoom(level, roomIndex) : undefined;
    if (ref) return { wall: findWall(level, ref), room };
    const side = parseSide(input);
    if (!room || !side) throw new ToolError('Name the wall (wall: "w2") or give room_index and side (N, E, S or W).');
    const wall = wallBySide(level, room, side);
    if (!wall) throw new ToolError(`Room #${room.index} has no ${side} wall.`);
    return { wall, room };
  }
  /** The room a wall serves: the one asked for, else the wall's own, else any room it bounds. */
  function roomOfWall(level: Level, wall: Wall, room?: Room): Room {
    if (room) return room;
    const own = level.rooms.find(candidate => candidate.index === wall.roomIndex && roomWalls(level, candidate).includes(wall)) ?? level.rooms.find(candidate => roomWalls(level, candidate).includes(wall));
    if (!own) throw new ToolError(`Wall ${aliases.alias('wall', wall.id)} does not bound a room; pieces need a room to stand in.`);
    return own;
  }
  const sideLabel = (room: Room, wall: Wall) => `${SIDE_LETTER[wallSide(room, wall)]} wall of #${room.index}`;
  /** One line per placed piece for a tool result: name, title, where. */
  const placedLine = (item: Item, name: Namer, note?: string) =>
    `${name('item', item.id)} "${item.caption || 'item'}" ${item.wallId ? `on ${name('wall', item.wallId)} h ${fmt(item.elevation)}` : `at ${point(item.position)} ${toDegrees(item.rotation)}°`}${note ? ` — ${note}` : ''}`;
  /** Commit a plan as items, one undo step for all of them. */
  function commitPlanned(planned: readonly Planned[]): Item[] {
    const items = planned.map(plan => {
      const entry = plan.entry;
      const item: Item = { id: 'item-' + crypto.randomUUID(), modelRef: modelRefOf(entry), caption: entry.title, placement: entry.placement,
        position: plan.position, elevation: plan.elevation, rotation: plan.rotation, size: { w: entry.size[0], h: entry.size[1], d: entry.size[2] }, scale: [1, 1, 1], mirrored: false, pivot: 'bottom',
        tags: [...entry.tags], ...(plan.wallId ? { wallId: plan.wallId } : {}) };
      return item;
    });
    store.runBatch(() => { for (const item of items) store.insertItem(item); if (items.length) store.set({ lastFloorPoint: items[items.length - 1].position }); });
    return items;
  }
  function findRoom(level: Level, index: number) {
    const room = level.rooms.find(value => value.index === index);
    if (!room) throw new ToolError(`No room #${index} on this floor. Rooms: ${level.rooms.map(value => `#${value.index}`).join(', ') || 'none'}.`);
    return room;
  }
  const roomLabel = (level: Level, position: Vec2) => {
    const room = roomOfPoint(level, position);
    return room ? `room #${room.index}` : 'outside every room';
  };

  function catalogLine(item: CatalogItem): string {
    return `- ${item.id} — ${item.title} · ${item.section} · ${fmt(item.size[0])}×${fmt(item.size[2])}×${fmt(item.size[1])} m (w×d×h) · ${item.placement}${item.converted ? '' : ' · simplified box, no 3D model yet'}`;
  }
  async function resolveCatalogItem(id: string, services: ToolServices): Promise<CatalogItem> {
    const catalog = await (services.catalog ?? loadAgentCatalog)();
    const wanted = id.replace(/^Models\//, '');
    const exact = catalog.items.find(item => item.id === wanted) ?? catalog.items.find(item => item.id.toLowerCase() === wanted.toLowerCase());
    if (exact) return exact;
    const near: CatalogItem[] = [];
    for (const query of [wanted, ...wanted.split(/[/_\-.]+/).filter(part => part && !/^\d+$/.test(part))]) for (const item of searchCatalog(catalog, query)) if (near.length < 5 && !near.includes(item)) near.push(item);
    throw new ToolError(`No catalog item "${id}".${near.length ? ` Similar: ${near.map(item => `${item.id} (${item.title})`).join('; ')}.` : ' Use search_catalog.'}`);
  }

  function parseColor(value: string): [number, number, number] {
    const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
    if (!match) throw new ToolError(`"color" must be #rrggbb, got "${value}".`);
    const hex = match[1];
    return [0, 2, 4].map(at => parseInt(hex.slice(at, at + 2), 16) / 255) as [number, number, number];
  }
  async function resolveSurface(input: Input, services: ToolServices): Promise<{ surface: Surface; label: string }> {
    const texture = string(input, 'texture'), color = string(input, 'color');
    if (texture) {
      const library = await (services.materials ?? loadAgentMaterials)();
      const wanted = texture.replace(/^\/hp\/tex\//, '');
      const entry = library.find(m => m.texture === wanted) ?? library.find(m => m.url === texture)
        ?? library.find(m => m.texture.endsWith('/' + wanted)) ?? library.find(m => m.caption.toLowerCase() === wanted.toLowerCase());
      if (!entry) {
        const near = library.filter(m => (m.caption + ' ' + m.texture).toLowerCase().includes(wanted.toLowerCase())).slice(0, 5);
        throw new ToolError(`No material "${texture}".${near.length ? ` Similar: ${near.map(m => `${m.texture} (${m.caption})`).join('; ')}.` : ' Use search_materials.'}`);
      }
      return { surface: textureSurface(entry), label: `${entry.caption} (${entry.collection})` };
    }
    if (color) return { surface: { color: parseColor(color), caption: color.startsWith('#') ? color : '#' + color }, label: color };
    throw new ToolError('Give a "texture" id from search_materials or a "color" like #d8c9a5.');
  }

  /** Where a wall item sits for a wall, a face and a distance along it — the drag gesture's own arithmetic. */
  function wallPlacement(level: Level, wallId: string, size: Item['size'], along: number | undefined, side: 'front' | 'back', elevation: number | undefined) {
    const wall = findWall(level, wallId);
    if (size.w > wall.length + 1e-6) throw new ToolError(`The item is ${fmt(size.w)} m wide but wall ${wall.id} is only ${fmt(wall.length)} m long.`);
    return { wall, ...wallItemPlacement(wall, size, along ?? wall.length / 2, side, elevation ?? 1.2) };
  }

  /** Separating-axis depth of two footprints; 0 when they only touch. */
  function penetrationDepth(a: readonly Vec2[], b: readonly Vec2[]): number {
    let depth = Infinity;
    for (const polygon of [a, b]) for (let i = 0; i < polygon.length; i++) {
      const p = polygon[i], q = polygon[(i + 1) % polygon.length];
      const length = Math.hypot(q.x - p.x, q.z - p.z) || 1, axis = { x: -(q.z - p.z) / length, z: (q.x - p.x) / length };
      const project = (points: readonly Vec2[]) => points.reduce((range, point) => { const value = point.x * axis.x + point.z * axis.z; return [Math.min(range[0], value), Math.max(range[1], value)]; }, [Infinity, -Infinity]);
      const [aMin, aMax] = project(a), [bMin, bMax] = project(b);
      const overlap = Math.min(aMax, bMax) - Math.max(aMin, bMin);
      if (overlap <= 0) return 0;
      depth = Math.min(depth, overlap);
    }
    return depth;
  }
  /** Furniture the piece runs into: the app's own obstruction rule for tabletop items, plus any floor item its body overlaps by more than a few centimetres. */
  function overlappingItems(items: readonly Item[], active: Item): string[] {
    const found = new Set(obstructions(items, active));
    if (active.placement === 'floor') {
      const footprint = itemFootprint(active);
      for (const other of items) {
        if (other.id === active.id || other.placement !== 'floor' || found.has(other.id)) continue;
        const vertical = Math.min(active.elevation + active.size.h, other.elevation + other.size.h) - Math.max(active.elevation, other.elevation);
        if (vertical <= .03 || Math.min(active.size.h, other.size.h) < .08) continue;
        if (penetrationDepth(footprint, itemFootprint(other)) > .03) found.add(other.id);
      }
    }
    return [...found];
  }

  return {
    async get_scene(input) {
      const scene = store.get().scene;
      if (!scene) throw new ToolError('No project is open.');
      const include = Array.isArray(input.include) ? input.include.filter((v): v is 'rooms' | 'walls' | 'openings' | 'items' => ['rooms', 'walls', 'openings', 'items'].includes(String(v))) : undefined;
      const listing = describeScene(scene, store.get().activeLevelIndex, { level: integer(input, 'level'), roomIndex: integer(input, 'room_index'), include, detail: choice(input, 'detail', ['compact', 'full']), projectId: store.get().projectId });
      return done(t('#AgentDoneReadScene'), listing);
    },

    async search_catalog(input, services) {
      const catalog = await (services.catalog ?? loadAgentCatalog)();
      const query = string(input, 'query'), section = string(input, 'section'), limit = integer(input, 'limit') ?? 20;
      const usable = usableCatalogItems(catalog.items);
      let items = usable;
      if (query) {
        const direct = new Set(searchCatalog(catalog, query));
        // A model searches in either language; the synonym rows make «диван» and "sofa" the same question.
        for (const term of expandQuery(query)) for (const item of searchCatalog(catalog, term)) direct.add(item);
        items = usable.filter(item => direct.has(item));
      }
      if (section) {
        const known = catalog.sections.find(s => s.id === section || s.title.toLowerCase() === section.toLowerCase());
        if (!known) throw new ToolError(`No section "${section}". Sections: ${catalog.sections.map(s => `${s.id} (${s.title})`).join(', ')}.`);
        items = items.filter(item => item.section === known.id);
      }
      const shown = items.slice(0, Math.max(1, limit));
      return done(t('#AgentDoneCatalogFound', items.length), items.length ? `Found in the catalog: ${items.length}${items.length > shown.length ? `, first ${shown.length}` : ''}:\n${shown.map(catalogLine).join('\n')}`
        : `Nothing in the catalog matches "${query ?? section}". The full catalog is in your instructions; sections: ${catalog.sections.map(s => `${s.id} (${s.title})`).join(', ')}. If the kind of item does not exist (there is no hob, rug or TV model), say so and use the closest item.`);
    },

    async search_materials(input, services) {
      const library = await (services.materials ?? loadAgentMaterials)();
      const surface = required(choice(input, 'surface', ['floor', 'wall', 'item']), 'surface');
      const query = string(input, 'query'), collection = string(input, 'collection'), limit = integer(input, 'limit') ?? 20;
      const { pool, matches } = matchMaterials(library, surface, query, collection);
      const shown = matches.slice(0, Math.max(1, limit));
      const collections = [...new Set(pool.map(m => m.collection))];
      return done(t('#AgentDoneMaterialsFound', matches.length), matches.length
        ? `Materials found: ${matches.length}${matches.length > shown.length ? `, first ${shown.length}` : ''} (texture id — caption · collection · tile):\n${shown.map(m => `- ${m.texture} — ${m.caption} · ${m.collection} · ${fmt(m.tile[0])}×${fmt(m.tile[1])} m`).join('\n')}`
        : `Nothing matched for ${surface}. Collections: ${collections.join(', ')}.`);
    },

    async add_room(input) {
      const level = activeLevel();
      const x = required(number(input, 'x'), 'x'), z = required(number(input, 'z'), 'z');
      const width = required(number(input, 'width'), 'width'), depth = required(number(input, 'depth'), 'depth');
      const name = string(input, 'name'), roomType = string(input, 'room_type');
      const before = new Set(level.rooms.map(room => room.index)), wallsBefore = new Set(level.walls.map(wall => wall.id));
      const result = store.runBatch(() => {
        const error = store.addRectangularRoom({ x, z }, { x: x + width, z: z + depth });
        if (error) return { error };
        const after = activeLevel();
        const centre = { x: x + width / 2, z: z + depth / 2 };
        const room = after.rooms.find(candidate => !before.has(candidate.index) && roomOfPoint(after, centre)?.index === candidate.index)
          ?? after.rooms.find(candidate => !before.has(candidate.index)) ?? roomOfPoint(after, centre);
        if (room && (name !== undefined || roomType !== undefined)) store.setRoomProperties(room.index, { name, roomType });
        const walls = activeLevel().walls.filter(wall => !wallsBefore.has(wall.id)).map(wall => wall.id);
        return { room: room ? activeLevel().rooms.find(candidate => candidate.index === room.index) : undefined, walls };
      });
      if ('error' in result) throw new ToolError(result.error);
      const room = result.room;
      const centre = { x: x + width / 2, z: z + depth / 2 };
      const n = names();
      const walls = room ? roomWalls(activeLevel(), room).map(wall => `${n('wall', wall.id)} ${SIDE_LETTER[wallSide(room, wall)]}`).join(', ') : result.walls.map(id => n('wall', id)).join(', ');
      return done(room ? t('#AgentDoneRoomAdded', room.index, room.name || t('Room')) : t('#AgentDoneWallsAdded'),
        room ? `Added room #${room.index} "${room.name || 'Room'}"${room.roomType ? ` (${room.roomType})` : ''}: ${fmt(width)}×${fmt(depth)} m from ${point({ x, z })}, ${fmt(room.area)} m². Walls: ${walls}.`
          : `Added walls ${walls}; no closed room was detected — check get_scene.`, centre);
    },

    async add_wall(input) {
      const start = { x: required(number(input, 'x1'), 'x1'), z: required(number(input, 'z1'), 'z1') };
      const end = { x: required(number(input, 'x2'), 'x2'), z: required(number(input, 'z2'), 'z2') };
      const before = new Set(activeLevel().walls.map(wall => wall.id));
      const error = store.runBatch(() => store.addWall(start, end));
      if (error) throw new ToolError(error);
      const added = activeLevel().walls.filter(wall => !before.has(wall.id));
      const n = names();
      return done(t('#AgentDoneWallAdded', point(start), point(end)), `Added wall ${point(start)}→${point(end)}: ${added.map(wall => n('wall', wall.id)).join(', ') || 'merged into existing walls'}. Rooms on the floor: ${activeLevel().rooms.length}.`, { x: (start.x + end.x) / 2, z: (start.z + end.z) / 2 });
    },

    async update_wall(input) {
      const level = activeLevel();
      const ref = required(string(input, 'id'), 'id');
      const id = findWall(level, ref).id;
      const thickness = number(input, 'thickness'), height = number(input, 'height'), length = number(input, 'length');
      const dx = number(input, 'move_x') ?? 0, dz = number(input, 'move_z') ?? 0;
      const error = store.runBatch(() => {
        if (thickness !== undefined || height !== undefined || length !== undefined) {
          const failed = store.applyWallProperties(id, { ...(thickness !== undefined ? { thickness } : {}), ...(height !== undefined ? { height } : {}), ...(length !== undefined ? { length } : {}) });
          if (failed) return failed;
        }
        return dx || dz ? store.moveWall(id, { x: dx, z: dz }) : null;
      });
      if (error) throw new ToolError(error);
      const wall = findWall(activeLevel(), id);
      const n = names();
      return done(t('#AgentDoneWallChanged', n('wall', id)), `Wall ${n('wall', id)}: ${point(wall.start)}→${point(wall.end)}, length ${fmt(wall.length)}, height ${fmt(wall.height)}, thickness ${fmt(wall.thickness)} m.`, wall.center);
    },

    async add_opening(input) {
      const level = activeLevel();
      const kind = required(choice(input, 'kind', ['door', 'window', 'arch']), 'kind') as OpeningKind;
      const { wall, room } = resolveWall(level, input);
      const wallId = wall.id;
      const width = number(input, 'width'), height = number(input, 'height'), sill = number(input, 'sill');
      const style = choice(input, 'style', ['solid', 'glazed', 'double-door', 'single', 'double', 'triple']);
      const at = input.at;
      const defaultWidth = { door: .9, window: 1.2, arch: 1.4 }[kind];
      let offset = number(input, 'offset');
      if (typeof at === 'number' || (typeof at === 'string' && /^[\d.]+$/.test(at.trim()))) offset = Number(at);
      else if (typeof at === 'string' && at.trim()) {
        const word = at.trim().toLowerCase();
        // i18n-ignore-start: words a model may pass for "at", in either language
        if (word === 'start' || word === 'начало') offset = .15 + (width ?? defaultWidth) / 2;
        else if (word === 'end' || word === 'конец') offset = wall.length - .15 - (width ?? defaultWidth) / 2;
        else if (word !== 'center' && word !== 'centre' && word !== 'middle' && word !== 'центр') throw new ToolError(`"at" must be start, center, end or metres from the wall's first point, got "${at}".`);
        // i18n-ignore-end
      }
      const before = new Set(level.openings.map(opening => opening.id));
      const result = store.runBatch(() => {
        const error = store.addOpening(wallId, kind, offset);
        if (error) return { error };
        // Between two rooms of a project from the app the opening comes with its copy on the other room's wall.
        const opening = activeLevel().openings.find(candidate => !before.has(candidate.id) && candidate.wallId === wallId);
        if (!opening) return { error: 'The opening was not created.' };
        if (width !== undefined || height !== undefined || sill !== undefined || style !== undefined) {
          const failed = store.applyOpeningProperties(opening.id, { ...(width !== undefined ? { width } : {}), ...(height !== undefined ? { height } : {}),
            ...(sill !== undefined ? { sill } : {}), ...(style !== undefined ? { design: { ...opening.design, style } } : {}) });
          if (failed) { store.removeOpenings([opening.id]); return { error: failed }; }
        }
        return { id: opening.id };
      });
      if ('error' in result) throw new ToolError(result.error);
      const opening = findOpening(activeLevel(), result.id);
      const onWall = findWall(activeLevel(), wallId);
      const n = names();
      const where = room ? ` (${sideLabel(room, onWall)})` : '';
      return done(t(kind === 'door' ? '#AgentDoneDoorAdded' : kind === 'window' ? '#AgentDoneWindowAdded' : '#AgentDoneArchAdded'), `Added ${kind} ${n('opening', opening.id)} on ${n('wall', wallId)}${where}: offset ${fmt(opening.offset)} m from the wall's first point, ${fmt(opening.width)}×${fmt(opening.height)} m, sill ${fmt(opening.sill)} m.`, { x: onWall.start.x + onWall.dir.x * opening.offset, z: onWall.start.z + onWall.dir.z * opening.offset });
    },

    async update_opening(input) {
      const level = activeLevel();
      const opening = findOpening(level, required(string(input, 'id'), 'id'));
      const id = opening.id;
      const values: Parameters<typeof store.applyOpeningProperties>[1] = {};
      for (const key of ['offset', 'width', 'height', 'sill'] as const) { const value = number(input, key); if (value !== undefined) values[key] = value; }
      const flipped = flag(input, 'flipped'); if (flipped !== undefined) values.flipped = flipped;
      const style = choice(input, 'style', ['solid', 'glazed', 'double-door', 'single', 'double', 'triple']);
      const openAngle = number(input, 'open_angle'), curtains = choice(input, 'curtains', ['none', 'sheer', 'drapes', 'roller']);
      if (style !== undefined || openAngle !== undefined || curtains !== undefined) values.design = { ...opening.design, ...(style !== undefined ? { style } : {}), ...(openAngle !== undefined ? { openAngle } : {}), ...(curtains !== undefined ? { curtains } : {}) };
      if (!Object.keys(values).length) throw new ToolError('Nothing to change: give offset, width, height, sill, flipped, style, open_angle or curtains.');
      const error = store.runBatch(() => store.applyOpeningProperties(id, values));
      if (error) throw new ToolError(error);
      const updated = findOpening(activeLevel(), id);
      const onWall = findWall(activeLevel(), updated.wallId);
      const n = names();
      return done(t('#AgentDoneOpeningChanged', n('opening', id)), `Opening ${n('opening', id)} on ${n('wall', updated.wallId)}: offset ${fmt(updated.offset)}, ${fmt(updated.width)}×${fmt(updated.height)} m, sill ${fmt(updated.sill)}${updated.flipped ? ', flipped' : ''}${updated.design?.style ? `, ${updated.design.style}` : ''}.`, { x: onWall.start.x + onWall.dir.x * updated.offset, z: onWall.start.z + onWall.dir.z * updated.offset });
    },

    async update_room(input) {
      const level = activeLevel();
      const index = required(integer(input, 'index'), 'index');
      findRoom(level, index);
      const name = string(input, 'name'), roomType = string(input, 'room_type');
      if (name === undefined && roomType === undefined) throw new ToolError('Give a name or a room_type.');
      const error = store.runBatch(() => store.setRoomProperties(index, { name, roomType }));
      if (error) throw new ToolError(error);
      const room = findRoom(activeLevel(), index);
      return done(t('#AgentDoneRoomChanged', index), `Room #${index} is now "${room.name}"${room.roomType ? ` (${room.roomType})` : ''}.`, room.polygon.length >= 3 ? interiorPoint(room.polygon) : null);
    },

    async add_item(input, services) {
      const entry = await resolveCatalogItem(required(string(input, 'catalog_id'), 'catalog_id'), services);
      const level = activeLevel();
      const size = { w: entry.size[0], h: entry.size[1], d: entry.size[2] };
      const x = number(input, 'x'), z = number(input, 'z');
      if ((x === undefined) !== (z === undefined)) throw new ToolError('Give both x and z, or neither.');
      const rotationInput = number(input, 'rotation'), elevationInput = number(input, 'elevation');
      const roomIndex = integer(input, 'room_index');
      let placed: Pick<Item, 'position' | 'elevation' | 'rotation' | 'wallId'>;
      let note = '';
      if (entry.placement === 'wall') {
        const hasWall = string(input, 'wall') ?? string(input, 'wall_id');
        if (!hasWall && (roomIndex === undefined || string(input, 'side') === undefined)) throw new ToolError(`"${entry.title}" hangs on a wall: name it (wall: "w2") or give room_index and side.`);
        const { wall } = resolveWall(level, input);
        const at = wallPlacement(level, wall.id, size, number(input, 'wall_offset'), choice(input, 'wall_side', ['front', 'back']) ?? 'front', elevationInput);
        placed = { position: at.position, elevation: at.elevation, rotation: at.rotation, wallId: at.wall.id };
      } else {
        const rotation = toRadians(rotationInput ?? 0);
        const room = roomIndex !== undefined ? findRoom(level, roomIndex) : undefined;
        const asked = x !== undefined && z !== undefined ? { x, z } : room ? interiorPoint(room.polygon) : null;
        const position = placementPoint(level, asked, size, rotation);
        if (asked && Math.hypot(position.x - asked.x, position.z - asked.z) > .011) note = ` Moved from ${point(asked)} to stay inside the room.`;
        placed = { position, rotation, elevation: entry.placement === 'ceiling' ? Math.max(level.height - size.h, 0) : Math.max(0, elevationInput ?? 0) };
      }
      const id = 'item-' + crypto.randomUUID();
      const item: Item = { id, modelRef: modelRefOf(entry), caption: entry.title, placement: entry.placement,
        position: placed.position, elevation: placed.elevation, rotation: placed.rotation, size, scale: [1, 1, 1], mirrored: false, pivot: 'bottom',
        tags: [...entry.tags], ...(placed.wallId ? { wallId: placed.wallId } : {}) };
      store.runBatch(() => { store.insertItem(item); store.set({ lastFloorPoint: placed.position }); });
      const after = activeLevel();
      const n = names();
      const blocking = overlappingItems(after.items, item).map(other => n('item', other));
      const where = placed.wallId ? `on ${n('wall', placed.wallId)}, bottom at ${fmt(placed.elevation)} m` : roomLabel(after, placed.position);
      return done(t('#AgentDoneItemAdded', entry.title), `Added ${n('item', id)} "${entry.title}" at ${point(placed.position)}, rotation ${toDegrees(placed.rotation)}°, size ${sizeOf(item)} (w×d×h), ${where}.${note}${entry.converted ? '' : ' Shown as a simplified box until its model is converted.'}${blocking.length ? ` Warning: overlaps ${blocking.join(', ')}.` : ''}`, placed.position);
    },

    async update_item(input) {
      const level = activeLevel();
      const item = findItem(level, required(string(input, 'id'), 'id'));
      const id = item.id;
      const x = number(input, 'x'), z = number(input, 'z'), moveX = number(input, 'move_x') ?? 0, moveZ = number(input, 'move_z') ?? 0;
      const rotationInput = number(input, 'rotation'), rotateBy = number(input, 'rotate_by');
      const width = number(input, 'width'), height = number(input, 'height'), depth = number(input, 'depth');
      const elevation = number(input, 'elevation'), mirrored = flag(input, 'mirrored');
      const wantsMove = x !== undefined || z !== undefined || moveX || moveZ;
      if (!wantsMove && rotationInput === undefined && rotateBy === undefined && width === undefined && height === undefined && depth === undefined && elevation === undefined && mirrored === undefined)
        throw new ToolError('Nothing to change.');
      for (const [key, value] of [['width', width], ['height', height], ['depth', depth]] as const) if (value !== undefined && value <= 0) throw new ToolError(`"${key}" must be positive.`);
      let note = '';
      const placed = wantsMove || rotationInput !== undefined || rotateBy !== undefined || width !== undefined || height !== undefined || depth !== undefined || elevation !== undefined;
      const locked = placed ? store.lockedItemsRefusal([id]) : null;
      if (locked) throw new ToolError(locked);
      const error = store.runBatch((): string | null => {
        let current = findItem(activeLevel(), id);
        if (width !== undefined || height !== undefined || depth !== undefined) {
          store.resizeItem(id, { ...(width !== undefined ? { w: width } : {}), ...(height !== undefined ? { h: height } : {}), ...(depth !== undefined ? { d: depth } : {}) });
          current = findItem(activeLevel(), id);
        }
        if (mirrored !== undefined && mirrored !== current.mirrored) {
          store.commitLevel('', level => ({ ...level, items: level.items.map(target => target.id === id ? { ...target, mirrored, scale: [-target.scale[0], target.scale[1], target.scale[2]] } : target) }));
          current = findItem(activeLevel(), id);
        }
        const target = { x: x ?? current.position.x + moveX, z: z ?? current.position.z + moveZ };
        let rotation = rotationInput !== undefined ? toRadians(rotationInput) : current.rotation;
        if (rotateBy !== undefined) rotation += toRadians(rotateBy);
        if (wantsMove || rotation !== current.rotation || elevation !== undefined) {
          if (current.placement === 'wall' && current.wallId) {
            const wall = findWall(activeLevel(), current.wallId);
            const along = (target.x - wall.start.x) * wall.dir.x + (target.z - wall.start.z) * wall.dir.z;
            const front = frontOf(current.rotation);
            const side = front.x * wall.normalIn.x + front.z * wall.normalIn.z >= 0 ? 'front' : 'back';
            const at = wallPlacement(activeLevel(), wall.id, current.size, along, side, elevation ?? current.elevation);
            store.commitLevel('', level => transformItemWithStack(level, id, { position: at.position, rotation: current.rotation, elevation: at.elevation }, undefined, store.surfaceProbe ?? undefined));
            if (rotationInput !== undefined || rotateBy !== undefined) note = ' Wall items keep facing their wall; rotation ignored.';
          } else {
            const room = roomOfPoint(activeLevel(), target) ?? roomOfPoint(activeLevel(), current.position);
            const probe = { ...current, position: target, rotation };
            const position = room ? constrainItemToRoom(probe, room.polygon, undefined, target) : target;
            if (wantsMove && Math.hypot(position.x - target.x, position.z - target.z) > .011) note = ` Kept inside room #${room!.index}: moved to ${point(position)} instead of ${point(target)}.`;
            store.commitLevel('', level => transformItemWithStack(level, id, { position, rotation, ...(elevation !== undefined ? { elevation: Math.max(0, elevation) } : {}) }, undefined, store.surfaceProbe ?? undefined));
          }
        }
        return null;
      });
      if (error) throw new ToolError(error);
      const updated = findItem(activeLevel(), id);
      const n = names();
      const blocking = overlappingItems(activeLevel().items, updated).map(other => n('item', other));
      return done(t('#AgentDoneItemChanged', updated.caption || n('item', id)), `${describeItem(updated, n).slice(2)}; ${updated.wallId ? `on ${n('wall', updated.wallId)}` : roomLabel(activeLevel(), updated.position)}.${note}${blocking.length ? ` Warning: overlaps ${blocking.join(', ')}.` : ''}`, updated.position);
    },

    async duplicate_item(input) {
      const level = activeLevel();
      const item = findItem(level, required(string(input, 'id'), 'id'));
      const id = item.id;
      const dx = number(input, 'move_x') ?? .3, dz = number(input, 'move_z') ?? .3;
      const copy = structuredClone(item);
      copy.id = 'item-' + crypto.randomUUID();
      copy.position = { x: item.position.x + dx, z: item.position.z + dz };
      store.runBatch(() => store.commitLevel('', level => ({ ...level, items: [...level.items, copy] }), [copy.id]));
      const n = names();
      return done(t('#AgentDoneItemDuplicated', item.caption || n('item', id)), `Added ${n('item', copy.id)} "${copy.caption || 'item'}" at ${point(copy.position)}, a copy of ${n('item', id)}.`, copy.position);
    },

    async replace_item(input, services) {
      const level = activeLevel();
      const item = findItem(level, required(string(input, 'id'), 'id'));
      const id = item.id;
      const entry = await resolveCatalogItem(required(string(input, 'catalog_id'), 'catalog_id'), services);
      const error = store.runBatch(() => store.applyProperties({ kind: 'item', id, values: { size: { w: entry.size[0], h: entry.size[1], d: entry.size[2] },
        position: item.position, elevation: item.elevation, rotation: item.rotation }, replacement: { ...entry, catalogId: entry.catalogId ?? entry.id, modelRef: modelRefOf(entry) } }));
      if (error) throw new ToolError(error);
      const updated = findItem(activeLevel(), id);
      const n = names();
      return done(t('#AgentDoneItemReplaced', entry.title), `${n('item', id)} is now "${entry.title}" [${entry.id}], ${sizeOf(updated)} at ${point(updated.position)}.`, updated.position);
    },

    async delete(input) {
      const level = activeLevel();
      const kind = required(choice(input, 'kind', ['item', 'wall', 'opening']), 'kind');
      const refs = Array.isArray(input.ids) ? input.ids.map(String) : [];
      if (!refs.length) throw new ToolError('"ids" must list at least one name.');
      const ids = refs.map(ref => kind === 'item' ? findItem(level, ref).id : kind === 'opening' ? findOpening(level, ref).id : findWall(level, ref).id);
      const locked = kind === 'item' ? store.lockedItemsRefusal(ids) : null;
      if (locked) throw new ToolError(locked);
      const removed = store.runBatch(() => {
        if (kind === 'item') return store.removeItems(ids);
        if (kind === 'opening') return store.removeOpenings(ids);
        let count = 0;
        for (const id of ids) { const error = store.removeWall(id); if (error) throw new ToolError(error); count++; }
        return count;
      });
      return done(t('#AgentDoneDeleted', kind, removed), `Removed ${removed} ${kind}${removed === 1 ? '' : 's'}: ${refs.join(', ')}. The floor now has ${activeLevel().walls.length} walls, ${activeLevel().openings.length} openings, ${activeLevel().items.length} items.`);
    },

    async set_material(input, services) {
      const level = activeLevel();
      const kind = required(choice(input, 'target', ['floor', 'ceiling', 'wall', 'item']), 'target');
      const all = flag(input, 'apply_to_all') ?? false;
      let target: NonNullable<MaterialTarget>, what: string;
      if (kind === 'floor' || kind === 'ceiling') {
        const index = integer(input, 'room_index') ?? (all ? level.rooms[0]?.index : undefined);
        if (index === undefined) throw new ToolError('Give room_index (or apply_to_all).');
        findRoom(level, index); target = { kind, roomIndex: index }; what = all ? `every ${kind} on the floor` : `${kind} of room #${index}`;
      } else if (kind === 'wall') {
        const ref = string(input, 'wall_id') ?? string(input, 'wall') ?? (all ? level.walls[0]?.id : undefined);
        if (!ref) throw new ToolError('Give wall_id (or apply_to_all).');
        const wallId = findWall(level, ref).id; const side = choice(input, 'side', ['front', 'back']) ?? 'front';
        target = { kind: 'wall', wallId, side }; what = all ? `the ${side} side of every wall` : `${side} side of wall ${aliases.alias('wall', wallId)}`;
      } else {
        const ref = required(string(input, 'item_id') ?? string(input, 'item'), 'item_id');
        const itemId = findItem(level, ref).id; const slot = string(input, 'slot') ?? '*';
        target = { kind: 'item', itemId, slot }; what = slot === '*' ? `item ${aliases.alias('item', itemId)} (whole)` : `slot "${slot}" of item ${aliases.alias('item', itemId)}`;
      }
      const { surface, label } = await resolveSurface(input, services);
      store.runBatch(() => store.setSurface(target, surface, all && kind !== 'item'));
      const anchor = target.kind === 'wall' ? findWall(level, target.wallId).center : target.kind === 'item' ? findItem(level, target.itemId).position : target.kind === 'floor' || target.kind === 'ceiling' ? (() => { const room = findRoom(level, target.roomIndex); return room.polygon.length >= 3 ? interiorPoint(room.polygon) : null; })() : null;
      return done(t('#AgentDoneMaterial', label), `Applied ${label} to ${what}.`, anchor);
    },

    async clear_level(input) {
      if (flag(input, 'confirm') !== true) throw new ToolError('Pass confirm: true to clear the floor.');
      const level = activeLevel();
      const counts = `${level.walls.length} walls, ${level.openings.length} openings, ${level.items.length} items`;
      store.runBatch(() => { store.commitLevel('', value => ({ ...value, walls: [], rooms: [], items: [], openings: [], outline: [] })); store.select(null); });
      return done(t('#AgentDoneFloorCleared'), `Cleared floor ${store.get().activeLevelIndex}: removed ${counts}. The user can undo this.`);
    },

    async set_view(input) {
      activeLevel();
      const mode = choice(input, 'mode', ['plan', 'orbit', 'walk']), planSurface = choice(input, 'plan_surface', ['floor', 'ceiling', 'wall']);
      const walls = choice(input, 'walls', ['full', 'cutaway', 'low']), showCeiling = flag(input, 'show_ceiling'), showDimensions = flag(input, 'show_dimensions');
      const isolate = input.isolate_room === null ? null : integer(input, 'isolate_room');
      if (typeof isolate === 'number') findRoom(activeLevel(), isolate);
      const patch: Parameters<typeof store.set>[0] = {};
      if (mode === 'plan') Object.assign(patch, { mode: 'plan', wallMode: 'full' });
      if (mode === 'orbit') Object.assign(patch, { mode: 'scene', firstPerson: false, showCeiling: false });
      if (mode === 'walk') Object.assign(patch, { mode: 'scene', firstPerson: true, wallMode: 'full', showCeiling: true });
      if (planSurface && planSurface !== 'wall') patch.planSurface = planSurface;
      if (walls !== undefined) patch.wallMode = walls;
      if (showCeiling !== undefined) patch.showCeiling = showCeiling;
      if (showDimensions !== undefined) patch.showDimensions = showDimensions;
      if (isolate !== undefined) patch.isolatedRoom = isolate;
      if (!Object.keys(patch).length && planSurface !== 'wall') throw new ToolError('Nothing to change.');
      store.set({ ...patch, showCatalog: false, showGallery: false, cameraMode: false });
      if (planSurface === 'wall') {
        // The wall plan picks its own wall (the app's default: the largest room's longest interior face).
        store.setPlanSurface('wall');
        if (store.get().planSurface !== 'wall') throw new ToolError('The wall plan needs a wall with a room behind it; draw a room first.');
      }
      const state = store.get();
      return done(t('#AgentDoneViewSwitched'), `View: ${state.mode === 'plan' ? `2D plan (${state.planSurface})` : state.firstPerson ? '3D walk' : '3D orbit'}${state.wallMode !== 'full' ? `, walls ${state.wallMode}` : ''}${state.showCeiling ? ', ceiling shown' : ''}${state.isolatedRoom !== null ? `, only room #${state.isolatedRoom}` : ''}.`);
    },

    async set_floor(input) {
      const scene = store.get().scene;
      if (!scene) throw new ToolError('No project is open.');
      const index = required(integer(input, 'index'), 'index');
      if (!scene.levels[index]) throw new ToolError(`No floor ${index}; the project has floors 0…${scene.levels.length - 1}.`);
      store.setActiveLevel(index);
      return done(t('#AgentDoneFloor', index), `Active floor: ${index} "${levelLabel(scene.levels, index)}".`);
    },

    async focus(input, services) {
      const level = activeLevel();
      const editor = (services.editor ?? editorAccess)();
      if (!editor) throw new ToolError('The editor view is not available.');
      const plan = store.get().mode === 'plan';
      if (flag(input, 'fit')) {
        if (plan) editor.fitPlan(); else { store.set({ firstPerson: false, showCeiling: false }); editor.scene3d()?.recenter(); }
        return done(t('#AgentDoneWholeFloor'), 'Framed the whole floor.');
      }
      const roomIndex = integer(input, 'room_index'), itemId = string(input, 'item_id'), x = number(input, 'x'), z = number(input, 'z');
      const room = roomIndex !== undefined ? findRoom(level, roomIndex) : undefined;
      const target = room ? interiorPoint(room.polygon) : itemId ? findItem(level, itemId).position : x !== undefined && z !== undefined ? { x, z } : undefined;
      if (!target) throw new ToolError('Give room_index, item_id, x and z, or fit: true.');
      let moved: boolean;
      if (plan) moved = editor.plan()?.revealPoint(target, editor.visibleArea()) ?? false;
      else if (room) { editor.scene3d()?.navigateToRoom(room); moved = true; }
      else moved = editor.scene3d()?.revealPoint(target, editor.visibleArea()) ?? false;
      return done(t('#AgentDoneCameraAimed'), `${moved ? 'Brought' : 'Already showing'} ${room ? `room #${room.index}` : itemId ? `item ${aliases.alias('item', findItem(level, itemId).id)}` : point(target)} in view.`);
    },

    async select(input) {
      const level = activeLevel();
      const kind = required(choice(input, 'kind', ['item', 'wall', 'opening', 'room', 'none']), 'kind');
      let selection: Selection = null;
      if (kind === 'item') selection = { kind, id: findItem(level, required(string(input, 'id'), 'id')).id };
      if (kind === 'wall') selection = { kind, id: findWall(level, required(string(input, 'id'), 'id')).id, side: choice(input, 'side', ['front', 'back']) ?? 'front' };
      if (kind === 'opening') selection = { kind, id: findOpening(level, required(string(input, 'id'), 'id')).id };
      if (kind === 'room') selection = { kind, index: findRoom(level, required(integer(input, 'index'), 'index')).index };
      store.set({ showCatalog: false, showProperties: false, materialTarget: null });
      store.select(selection, null);
      return done(selection ? t('#AgentDoneSelected') : t('#AgentDoneSelectionCleared'), selection ? `Selected ${kind} ${'id' in selection ? aliases.alias(kind === 'item' ? 'item' : kind === 'opening' ? 'opening' : 'wall', selection.id) : '#' + selection.index}.` : 'Selection cleared.');
    },

    async capture_view(input, services) {
      activeLevel();
      const editor = (services.editor ?? editorAccess)();
      if (!editor) throw new ToolError('The editor view is not available.');
      const maxSide = Math.max(256, Math.min(1600, integer(input, 'max_side') ?? 1024));
      const captured = await captureViewImage(editor, maxSide);
      if (!captured) throw new ToolError('Nothing to capture yet — the view has not drawn a frame.');
      const state = store.get();
      const data = await blobToBase64(captured.blob);
      return { summary: state.mode === 'plan' ? t('#AgentDonePlanSnapshot') : t('#AgentDoneViewSnapshot'), content: [text(`${captured.label} of "${state.projectName}", floor ${state.activeLevelIndex}:`), { type: 'image', mediaType: 'image/jpeg', data }] };
    },

    async render_ai(input, services) {
      activeLevel();
      if (!services.render) throw new ToolError('AI render is not available in this session.');
      const type = required(choice(input, 'type', ['photo', 'redesign']), 'type');
      const style = string(input, 'style'), prompt = string(input, 'prompt'), quality = choice(input, 'quality', ['HD', 'FullHD']);
      const result = await services.render({ type, style, prompt, quality }, services.signal);
      return done(t('#AgentDoneRenderReady'), `The ${type === 'photo' ? 'AI photo' : `redesign${style ? ` (${style})` : ''}`} is ready after ${Math.round(result.elapsedMs / 1000)} s: ${result.imageUrl}. It is in the project gallery (show_panel gallery).`);
    },

    async history(input) {
      activeLevel();
      const action = required(choice(input, 'action', ['undo', 'redo']), 'action');
      const steps = Math.max(1, integer(input, 'steps') ?? 1);
      let count = 0;
      if (store.isShared) {
        count = Math.min(steps, action === 'undo' ? store.historyDepth : store.redoDepth);
        if (count) { if (action === 'undo') store.undo(count); else store.redo(count); }
        return done(t(action === 'undo' ? '#AgentDoneUndone' : '#AgentDoneRedone', count), count ? `${action === 'undo' ? 'Undid' : 'Redid'} ${count} step${count === 1 ? '' : 's'}.` : `Nothing to ${action}.`);
      }
      for (let i = 0; i < steps; i++) {
        if (action === 'undo' ? !store.canUndo : !store.canRedo) break;
        if (action === 'undo') store.undo(); else store.redo();
        count++;
      }
      return done(t(action === 'undo' ? '#AgentDoneUndone' : '#AgentDoneRedone', count), count ? `${action === 'undo' ? 'Undid' : 'Redid'} ${count} step${count === 1 ? '' : 's'}.` : `Nothing to ${action}.`);
    },

    async project(input, services) {
      const action = required(choice(input, 'action', ['list', 'open', 'create', 'rename']), 'action');
      const app = (services.app ?? appAccess)();
      if (action === 'list') {
        const local = typeof localStorage === 'undefined' ? [] : localProjects();
        const current = store.get().projectId;
        const lines = [
          local.length ? `Projects in this browser (${local.length}):` : 'No projects saved in this browser yet.',
          ...local.map(project => `- ${project.id}${project.id === current ? ' (open now)' : ''} — "${project.name}", ${fmt(project.area)} m², saved ${new Date(project.savedAt).toISOString().slice(0, 10)}`),
          'Ready-made projects:', ...demoProjects.map(demo => `- ${demo.id}${demo.id === current ? ' (open now)' : ''} — "${demo.name}", ${demo.floors} floor(s), ${demo.rooms} rooms, ${demo.area} m²`),
        ];
        return done(t('#AgentDoneProjectList'), lines.join('\n'));
      }
      if (action === 'rename') {
        activeLevel();
        const name = required(string(input, 'name'), 'name');
        store.set({ projectName: name }); store.saveLocal();
        return done(t('#AgentDoneRenamed', name), `The project is now called "${name}".`);
      }
      if (!app) throw new ToolError('Project navigation is not available here.');
      if (action === 'create') {
        const created = app.createProject(string(input, 'name') ?? t('#AgentNewProjectName'));
        return done(t('#AgentDoneProjectCreated', created.name), `Created and opened an empty project ${created.id} "${created.name}". Add a room with add_room to start.`);
      }
      const id = required(string(input, 'id'), 'id');
      const known = (typeof localStorage === 'undefined' ? [] : localProjects()).find(project => project.id === id) ?? demoProjects.find(demo => demo.id === id);
      app.openProject(id, known?.name ?? t('#AgentProjectFallbackName'));
      return done(t('#AgentDoneOpening', known?.name ?? id), `Opening project ${id}${known ? ` "${known.name}"` : ''}. Call get_scene once it has loaded.`);
    },

    async show_panel(input) {
      activeLevel();
      const panel = required(choice(input, 'panel', ['catalog', 'gallery', 'properties', 'material', 'lighting', 'minimap', 'camera', 'none']), 'panel');
      const state = store.get();
      const closed = { showCatalog: false, showGallery: false, showProperties: false, materialTarget: null, showLighting: false, showMinimap: false, cameraMode: false, showVariations: false } as const;
      if (panel === 'none') { store.set(closed); return done(t('#AgentDonePanelsClosed'), 'Every panel is closed.'); }
      if (panel === 'catalog') store.set({ ...closed, showCatalog: true });
      else if (panel === 'gallery') store.set({ ...closed, showGallery: true });
      else if (panel === 'minimap') store.set({ ...closed, showMinimap: true });
      else if (panel === 'lighting' || panel === 'camera') {
        if (state.mode === 'plan') throw new ToolError(`${panel} is a 3D panel: switch the view with set_view first.`);
        store.set({ ...closed, ...(panel === 'lighting' ? { showLighting: true } : { cameraMode: true }) });
      } else {
        const selection = state.selection;
        if (!selection || selection.kind === 'vertex') throw new ToolError(`Select an item, wall, opening or room first (select tool), then open ${panel}.`);
        if (panel === 'properties') store.set({ ...closed, showProperties: true });
        else {
          const level = activeLevel();
          const target: MaterialTarget = selection.kind === 'item' ? { kind: 'item', itemId: selection.id, slot: '*' }
            : selection.kind === 'wall' ? { kind: 'wall', wallId: selection.id, side: selection.side }
            : selection.kind === 'room' ? { kind: state.mode === 'plan' && state.planSurface === 'ceiling' ? 'ceiling' : 'floor', roomIndex: selection.index } : null;
          if (!target) throw new ToolError('Openings have no material sheet; select an item, wall or room.');
          if (target.kind === 'floor' || target.kind === 'ceiling') findRoom(level, target.roomIndex);
          store.set({ ...closed, materialTarget: target });
        }
      }
      return done(t('#AgentDonePanelOpened', panel), `Opened the ${panel} panel for the user.`);
    },

    async place_along_wall(input, services) {
      const level = activeLevel();
      const { wall, room: asked } = resolveWall(level, input);
      const room = roomOfWall(level, wall, asked);
      const ids = Array.isArray(input.items) ? input.items.map(String) : typeof input.items === 'string' ? [input.items] : [];
      if (!ids.length) throw new ToolError('"items" must list at least one catalog id.');
      const entries = [];
      for (const id of ids) entries.push(await resolveCatalogItem(id, services));
      const plan = planRun(level, room, wall, entries, { from: choice(input, 'from', ['start', 'end', 'center']), offset: number(input, 'offset'), gap: number(input, 'gap'), elevation: number(input, 'elevation') });
      if (plan.error) throw new ToolError(plan.error);
      const items = commitPlanned(plan.placed);
      const n = names();
      const lines = items.map((item, i) => placedLine(item, n, plan.placed[i].note && plan.placed[i].note!.replace(/item-[0-9a-f-]+/g, id => n('item', id))));
      return done(t('#AgentDonePlacedAlongWall', items.length), `Along ${n('wall', wall.id)} (${sideLabel(room, wall)}), fronts into the room: ${lines.join('; ')}.`, wall.center);
    },

    async place_beside(input, services) {
      const level = activeLevel();
      const anchor = findItem(level, required(string(input, 'anchor'), 'anchor'));
      const entry = await resolveCatalogItem(required(string(input, 'catalog_id'), 'catalog_id'), services);
      if (entry.placement === 'wall') throw new ToolError(`"${entry.title}" hangs on a wall: use add_item with a wall, or place_along_wall.`);
      const side = required(choice(input, 'side', ['left', 'right', 'front', 'behind', 'both']), 'side');
      const coffee = /coffee|журнальн/i.test(entry.title + ' ' + entry.tags.join(' ')); // i18n-ignore: catalog titles and tags
      const gap = number(input, 'gap') ?? (side === 'front' && coffee ? .45 : .05);
      const room = roomOfItem(level, anchor);
      const sides = side === 'both' ? ['left', 'right'] as const : [side];
      const planned: Planned[] = [], refused: string[] = [];
      for (const one of sides) {
        const result = planBeside(level, room, anchor, entry, one, gap, planned);
        if (result.placed) planned.push(result.placed); else refused.push(result.error ?? `${one}: no room`);
      }
      if (!planned.length) throw new ToolError(refused.map(text => text.replace(anchor.id, aliases.alias('item', anchor.id))).join(' '));
      const items = commitPlanned(planned);
      const n = names();
      const lines = items.map((item, i) => placedLine(item, n, planned[i].note && planned[i].note!.replace(/item-[0-9a-f-]+/g, id => n('item', id))));
      return done(t('#AgentDonePlacedBeside', entry.title), `Beside ${n('item', anchor.id)} "${anchor.caption || ''}": ${lines.join('; ')}.${refused.length ? ` Not placed: ${refused.map(text => text.replace(anchor.id, n('item', anchor.id))).join(' ')}` : ''}`, items[0].position);
    },

    async arrange_around(input, services) {
      const level = activeLevel();
      const table = findItem(level, required(string(input, 'table'), 'table'));
      const entry = await resolveCatalogItem(required(string(input, 'catalog_id'), 'catalog_id'), services);
      if (entry.placement !== 'floor') throw new ToolError(`"${entry.title}" is not a floor item.`);
      const count = Math.max(1, Math.min(12, integer(input, 'count') ?? 4));
      const planned = planAround(level, roomOfItem(level, table), table, entry, count);
      if (!planned.length) throw new ToolError(`No "${entry.title}" fits around ${aliases.alias('item', table.id)} inside its room.`);
      const items = commitPlanned(planned);
      const n = names();
      const lines = items.map((item, i) => placedLine(item, n, planned[i].note && planned[i].note!.replace(/item-[0-9a-f-]+/g, id => n('item', id))));
      return done(t('#AgentDoneArrangedAround', items.length), `${items.length} of ${count} around ${n('item', table.id)} "${table.caption || ''}", facing it: ${lines.join('; ')}.${items.length < count ? ` ${count - items.length} would leave the room.` : ''}`, table.position);
    },

    async furnish_room(input, services) {
      const level = activeLevel();
      const room = findRoom(level, required(integer(input, 'room_index') ?? integer(input, 'room'), 'room_index'));
      const hint = `${room.roomType} ${room.name}`.toLowerCase();
      // i18n-ignore: words matched in the room's type and name, in either language
      const guessed: Preset | undefined = PRESET_FOR_ROOM_TYPE[room.roomType] ?? (/спальн|bed/.test(hint) ? 'bedroom' : /детск|kid|child/.test(hint) ? 'kids' : /кухн|kitchen/.test(hint) ? 'kitchen' : /гостин|living|зал/.test(hint) ? 'living' : /столов|dining/.test(hint) ? 'dining' : /ванн|bath|санузел|туалет|wc/.test(hint) ? 'bathroom' : /кабинет|office|study/.test(hint) ? 'office' : undefined);
      const preset = choice(input, 'preset', FURNISH_PRESETS) ?? guessed;
      if (!preset) throw new ToolError(`Room #${room.index} has no type: pass preset (${FURNISH_PRESETS.join(', ')}).`);
      const side = parseSide(input);
      const catalog = await (services.catalog ?? loadAgentCatalog)();
      const plan = planPreset(level, room, preset, catalog.items, side);
      if (!plan.placed.length) throw new ToolError(`Nothing fits in room #${room.index} as a ${preset}: ${plan.skipped.join('; ') || 'the room is too small'}.`);
      const roomType = { bedroom: 'bedroom', kids: 'kids', living: 'livingroom', dining: 'diningroom', kitchen: 'kitchen', bathroom: 'bathroom', office: 'office' }[preset];
      const items = store.runBatch(() => {
        const placed = commitPlanned(plan.placed);
        if (!room.roomType) store.setRoomProperties(room.index, { roomType });
        return placed;
      });
      const n = names();
      const lines = items.map((item, i) => `${placedLine(item, n)}${plan.placed[i].role ? ` (${plan.placed[i].role})` : ''}`);
      return done(plural('#AgentDoneRoomFurnished', items.length, room.index), `Furnished room #${room.index} as ${preset} (${items.length} pieces): ${lines.join('; ')}.${plan.skipped.length ? ` Skipped: ${plan.skipped.join('; ')}.` : ''} Change single pieces one by one; do not furnish this room again.`, room.polygon.length >= 3 ? interiorPoint(room.polygon) : null);
    },

    async set_room_finish(input, services) {
      const level = activeLevel();
      const all = flag(input, 'apply_to_all') ?? false;
      const roomIndex = integer(input, 'room_index') ?? integer(input, 'room') ?? (all ? level.rooms[0]?.index : undefined);
      if (roomIndex === undefined) throw new ToolError('Give room_index (or apply_to_all).');
      const room = findRoom(level, roomIndex);
      const floorQuery = string(input, 'floor'), wallsQuery = string(input, 'walls');
      const floorColor = string(input, 'floor_color'), wallColor = string(input, 'wall_color'), ceilingColor = string(input, 'ceiling_color');
      if (!floorQuery && !wallsQuery && !floorColor && !wallColor && !ceilingColor) throw new ToolError('Give floor and/or walls (words), or floor_color / wall_color / ceiling_color.');
      const library = floorQuery || wallsQuery ? await (services.materials ?? loadAgentMaterials)() : [];
      const pick = (surface: 'floor' | 'wall', query: string): { surface: Surface; label: string } | null => {
        const hit = matchMaterials(library, surface, query).matches[0];
        return hit ? { surface: textureSurface(hit), label: `${hit.caption} (${hit.collection})` } : null;
      };
      const floor = floorColor ? { surface: { color: parseColor(floorColor), caption: floorColor } as Surface, label: floorColor } : floorQuery ? pick('floor', floorQuery) : null;
      const walls = wallColor ? { surface: { color: parseColor(wallColor), caption: wallColor } as Surface, label: wallColor } : wallsQuery ? pick('wall', wallsQuery) : null;
      const ceiling = ceilingColor ? { surface: { color: parseColor(ceilingColor), caption: ceilingColor } as Surface, label: ceilingColor } : null;
      const missing = [floorQuery && !floor ? `no floor finish matches "${floorQuery}"` : '', wallsQuery && !walls ? `no wall finish matches "${wallsQuery}"` : ''].filter(Boolean);
      if (!floor && !walls && !ceiling) throw new ToolError(`${missing.join('; ')}. Try other words or search_materials.`);
      const rooms = all ? level.rooms : [room];
      const applied: string[] = [];
      store.runBatch(() => {
        for (const target of rooms) {
          if (floor) store.setSurface({ kind: 'floor', roomIndex: target.index }, floor.surface);
          if (ceiling) store.setSurface({ kind: 'ceiling', roomIndex: target.index }, ceiling.surface);
          if (walls) for (const wall of roomWalls(level, target)) {
            const inward = inwardNormal(target, wall);
            const side = inward.x * wall.normalIn.x + inward.z * wall.normalIn.z > 0 ? 'front' : 'back';
            store.setSurface({ kind: 'wall', wallId: wall.id, side }, walls.surface);
          }
        }
        if (floor) applied.push(`floor: ${floor.label}`);
        if (walls) applied.push(`walls: ${walls.label}`);
        if (ceiling) applied.push(`ceiling: ${ceiling.label}`);
      });
      const where = all ? `every room on the floor` : `room #${room.index}`;
      return done(t('#AgentDoneFinish', applied.map(text => text.split(': ')[1]).join(', ')), `Applied to ${where} — ${applied.join('; ')}.${missing.length ? ` ${missing.join('; ')}.` : ''}`, room.polygon.length >= 3 ? interiorPoint(room.polygon) : null);
    },

    async delegate(input, services) {
      if (!services.delegate) throw new ToolError('Delegation is not available for this agent.');
      const listed = Array.isArray(input.tasks) ? input.tasks.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object') : [];
      const tasks = listed.map(entry => ({ agentId: required(string(entry, 'agent_id'), 'tasks[].agent_id'), task: required(string(entry, 'task'), 'tasks[].task') }));
      if (!tasks.length) tasks.push({ agentId: required(string(input, 'agent_id'), 'agent_id'), task: required(string(input, 'task'), 'task') });
      const unknown = tasks.filter(entry => !roleById(entry.agentId)).map(entry => `"${entry.agentId}"`);
      if (unknown.length) throw new ToolError(`No subagent ${unknown.join(', ')}. Subagents: ${ROLE_IDS.join(', ')}.`);
      const twice = tasks.map(entry => entry.agentId).filter((id, index, ids) => ids.indexOf(id) !== index);
      if (twice.length) throw new ToolError(`One task per agent in a call: ${[...new Set(twice)].join(', ')} named more than once. Give each subagent one task with everything it needs.`);
      // Several tasks run side by side: each subagent gets its own loop and its own cursor; their edits commit one at a time.
      const reports = await Promise.all(tasks.map(async ({ agentId, task }) => {
        const name = roleById(agentId)!.name;
        try { return { agentId, name, report: await services.delegate!(agentId, task) }; }
        catch (error) { return { agentId, name, report: `Failed: ${error instanceof Error ? error.message : String(error)}`, failed: true }; }
      }));
      if (reports.length === 1) { if (reports[0].failed) throw new ToolError(reports[0].report); return done(t('#AgentDoneReport', reports[0].name), reports[0].report); }
      const failed = reports.filter(entry => entry.failed).length, names = reports.map(entry => entry.name).join(', ');
      return { content: [text(reports.map(entry => `## ${entry.agentId}\n${entry.report}`).join('\n\n'))], summary: failed ? t('#AgentDoneReportsFailed', names, failed) : t('#AgentDoneReports', names), ...(failed === reports.length ? { isError: true } : {}) };
    },
  };

}

type CachedCommand = { fingerprint: string; result: Promise<ToolOutcome>; settled: boolean; pending?: boolean; scope: DocumentScope | null; shared: boolean };
const commandResults = new Map<string, CachedCommand>();
const COMMAND_MESSAGES: Record<NonNullable<CommandOutcome['code']>, TranslationKey> = {
  scope_conflict: '#AgentCommandScopeConflict',
  revision_conflict: '#AgentCommandRevisionConflict',
  busy: '#AgentCommandBusy',
  cancelled: '#AgentActionCancelled', duplicate_mismatch: '#AgentCommandDuplicate',
  forbidden: '#StoreAccessClosed', outside_rights: '#AgentCommandForbidden', invalid_command: '#AgentCommandInvalid', tool_error: '#AgentCommandFailed',
  conflict: '#AgentCommandConflict', invalid: '#AgentCommandRejected',
};
function validCommand(command: CommandMetadata): boolean {
  const id = (value: unknown) => typeof value === 'string' && value.trim().length > 0 && value.length <= 256;
  if (!command || !id(command.commandId) || !id(command.callerId) || (command.turnId !== undefined && !id(command.turnId))) return false;
  const scope = command.scope;
  return scope === undefined || (!!scope && id(scope.projectId)
    && [scope.epoch, scope.levelIndex, scope.scopeVersion, scope.revision].every(value => Number.isSafeInteger(value) && value >= 0));
}
function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (value && typeof value === 'object') return '{' + Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => JSON.stringify(key) + ':' + canonical(item)).join(',') + '}';
  return JSON.stringify(value) ?? 'undefined';
}
function commandFailure(commandId: string, scope: DocumentScope | null, code: NonNullable<CommandOutcome['code']>, message = t(COMMAND_MESSAGES[code]), current = liveStore.captureDocumentScope()): ToolOutcome {
  return { content: [text(message)], isError: true, summary: message,
    command: { commandId, status: code === 'cancelled' ? 'cancelled' : 'rejected', scope: current,
      baseRevision: scope?.revision ?? 0, revision: current?.revision ?? 0, code } };
}
function commandResultScope(origin: DocumentScope | null): DocumentScope | null {
  const current = liveStore.captureDocumentScope();
  return current && origin && current.projectId === origin.projectId && current.epoch === origin.epoch
    && current.levelIndex === origin.levelIndex && current.scopeVersion === origin.scopeVersion ? current : origin;
}
export function abortable<T>(work: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return work;
  return new Promise((resolve, reject) => {
    const cancel = () => { reject(new Error(t(COMMAND_MESSAGES.cancelled))); };
    work.then(resolve, reject).finally(() => signal.removeEventListener('abort', cancel));
    if (signal.aborted) cancel();
    else signal.addEventListener('abort', cancel, { once: true });
  });
}

/** Identified calls share their original outcome; only a successfully prepared scene is adopted. */
export async function runTool(name: string, rawInput: unknown, services: ToolServices = {}): Promise<ToolOutcome> {
  const supplied = services.command === undefined ? { commandId: crypto.randomUUID(), callerId: 'editor' } : services.command;
  if (!validCommand(supplied)) return commandFailure(typeof supplied?.commandId === 'string' ? supplied.commandId : '', null, 'invalid_command');
  const command = { ...supplied, ...(supplied.scope ? { scope: { ...supplied.scope } } : {}) };
  let scope = command.scope ?? liveStore.captureDocumentScope();
  if (services.allowedTools && !services.allowedTools.includes(name)) return commandFailure(command.commandId, scope, 'outside_rights');
  if (!toolDefinition(name)) return commandFailure(command.commandId, scope, 'tool_error', `Unknown tool "${name}".`);
  let input: Input;
  let fingerprint: string;
  try {
    input = rawInput && typeof rawInput === 'object' && !Array.isArray(rawInput) ? structuredClone(rawInput) as Input : {};
    fingerprint = canonical({ name, input, scope: command.scope });
  }
  catch { return commandFailure(command.commandId, scope, 'invalid_command'); }
  const key = canonical([command.callerId, command.commandId]);
  const cached = commandResults.get(key);
  if (cached && cached.fingerprint !== fingerprint) return commandFailure(command.commandId, scope, 'duplicate_mismatch');
  if (cached && !cached.pending) return cached.result;
  if (cached) {
    scope = cached.scope;
    if (scope?.projectId !== liveStore.get().projectId || !liveStore.isShared) return cached.result;
  }
  const sharedCommand = cached?.shared ?? liveStore.isShared;
  const fail = (code: NonNullable<CommandOutcome['code']>, message?: string) => commandFailure(command.commandId, scope, code, message,
    sharedCommand ? commandResultScope(scope) : liveStore.captureDocumentScope());
  let resolve!: (outcome: ToolOutcome) => void;
  const result = new Promise<ToolOutcome>(done => { resolve = done; });
  const entry: CachedCommand = { fingerprint, result, settled: false, scope, shared: sharedCommand };
  commandResults.set(key, entry);
  const execute = async (): Promise<ToolOutcome> => {
    const effect = toolEffect(name, input), guarded = effect === 'document' || effect === 'history';
    const request = JSON.parse(JSON.stringify({ name, input, ...(command.scope ? { scope: command.scope } : {}) })) as Json;
    if (guarded && liveStore.isShared && scope?.projectId === liveStore.get().projectId) {
      try {
        const receipt = await liveStore.lookupSharedReceipt(command, request);
        if (receipt) {
          if (receipt.status === 'conflict') return fail('conflict');
          const saved = receipt.result as unknown as ToolOutcome | undefined;
          const current = commandResultScope(scope);
          return { ...(saved ?? done(t('#AgentDoneChangeSaved'))), command: { commandId: command.commandId, status: 'committed', scope: current,
            baseRevision: scope?.revision ?? 0, revision: current?.revision ?? 0, acceptedRevision: receipt.acceptedRevision ?? undefined } };
        }
        // An unknown receipt cannot turn an already dispatched identity into a new edit.
        if (cached?.pending) return cached.result;
      } catch (error) {
        const value = error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
        if (cached?.pending && !['duplicate_mismatch', 'forbidden'].includes(value)) return cached.result;
        return fail(value === 'duplicate_mismatch' ? 'duplicate_mismatch' : value === 'forbidden' ? 'forbidden' : 'tool_error');
      }
    }
    const failure = guarded ? liveStore.documentConflict(scope, services.signal) : services.signal?.aborted ? 'cancelled' : null;
    if (failure) return fail(failure);
    const draft = effect === 'document' && name !== 'project' ? liveStore.createDocumentDraft() : null;
    const base = draft ? liveStore.level : undefined;
    const target = draft ?? liveStore;
    const state = target.get();
    const aliases = aliasRegistry.scoped(aliasScope(state.projectId, state.activeLevelIndex), target.level);
    const executor = createExecutors(target, aliases)[name];
    if (!executor) return commandFailure(command.commandId, scope, 'tool_error', `Unknown tool "${name}".`);
    const live = liveStore.get();
    aliasRegistry.enter(aliasScope(live.projectId, live.activeLevelIndex));
    if (liveStore.level) aliasRegistry.sync(liveStore.level);
    try { agentCursor.begin(name, input, liveStore.level, services.agent); } catch { /* cursor is presentation only */ }
    let outcome: ToolOutcome;
    try {
      let appliedScope: DocumentScope | null = null;
      let expectedResult: ToolOutcome | undefined;
      if (name === 'project' && input.action === 'rename') {
        const name = required(string(input, 'name'), 'name');
        expectedResult = done(t('#AgentDoneRenamed', name), `The project is now called "${name}".`);
      } else if (name === 'history' && liveStore.isShared) {
        const action = required(choice(input, 'action', ['undo', 'redo']), 'action'), count = Math.min(Math.max(1, integer(input, 'steps') ?? 1),
          action === 'undo' ? liveStore.historyDepth : liveStore.redoDepth);
        expectedResult = done(t(action === 'undo' ? '#AgentDoneUndone' : '#AgentDoneRedone', count), count ? `${action === 'undo' ? 'Undid' : 'Redid'} ${count} step${count === 1 ? '' : 's'}.` : `Nothing to ${action}.`);
      }
      const owner = { ...command, request, ...(expectedResult ? { result: JSON.parse(JSON.stringify(expectedResult)) as Json } : {}) };
      const running = guarded && !draft ? liveStore.withDocumentOwner(owner, () => {
        const result = executor(input, services);
        appliedScope = liveStore.captureDocumentScope();
        return result;
      }) : executor(input, services);
      outcome = await (draft ? abortable(running, services.signal) : running);
      let decision: CommitDecision | null = null;
      const refused = draft && !outcome.isError && base && draft.level && services.rights?.(base, draft.level);
      if (refused) outcome = { ...fail('outside_rights', refused), summary: t('#AgentRightsRefused') };
      else if (draft && !outcome.isError) {
        decision = await liveStore.adoptDocumentDraftAccepted(draft, scope!, { ...owner, result: JSON.parse(JSON.stringify(outcome)) as Json }, services.signal);
      }
      else if (guarded && sharedCommand && !outcome.isError) {
        const accepted = liveStore.awaitDocumentCommand(command.commandId), signal = services.signal;
        decision = !signal ? await accepted : await new Promise<CommitDecision>(resolve => {
          const aborted = () => resolve({ status: 'pending', commandId: command.commandId });
          signal.addEventListener('abort', aborted, { once: true });
          void accepted.then(resolve).finally(() => signal.removeEventListener('abort', aborted));
          if (signal.aborted) aborted();
        });
      }
      if (decision?.status === 'rejected') outcome = fail(decision.code === 'conflict' && !sharedCommand ? 'revision_conflict' : decision.code);
      else if (decision?.status === 'pending') {
        const current = commandResultScope(scope);
        outcome = { summary: t('#AgentCheckingSave'), content: [text('The command was dispatched. Its outcome is pending; retry this same command identity to check the receipt.')],
          command: { commandId: command.commandId, status: 'pending', scope: current, baseRevision: scope?.revision ?? 0, revision: current?.revision ?? 0 } };
      }
      if (guarded && !outcome.command) {
        const current = sharedCommand ? commandResultScope(scope) : draft ? liveStore.captureDocumentScope() : appliedScope;
        outcome = { ...outcome, command: { commandId: command.commandId, status: outcome.isError ? 'rejected' : decision?.status ?? (current?.revision === scope?.revision ? 'unchanged' : 'committed'),
          scope: current, baseRevision: scope?.revision ?? 0, revision: current?.revision ?? 0,
          ...(sharedCommand && decision?.status === 'committed' ? { acceptedRevision: decision.revision } : {}), ...(outcome.isError ? { code: 'tool_error' as const } : {}) } };
      }
    } catch (error) {
      outcome = fail(services.signal?.aborted ? 'cancelled' : 'tool_error', services.signal?.aborted ? t(COMMAND_MESSAGES.cancelled) : error instanceof Error ? error.message : String(error));
    }
    const shown = (!outcome.isError || outcome.command?.code === 'outside_rights') && outcome.command?.status !== 'pending';
    try { agentCursor.finish(name, outcome.summary, outcome.anchor, shown, services.agent); } catch { /* cursor cannot change the outcome */ }
    return outcome;
  };
  void execute().catch(error => fail('tool_error', error instanceof Error ? error.message : String(error))).then(outcome => {
    entry.settled = true;
    entry.pending = outcome.command?.status === 'pending';
    resolve(outcome);
    let settled = [...commandResults.values()].filter(value => value.settled).length;
    for (const [oldKey, old] of commandResults) if (settled > 256 && old.settled) { commandResults.delete(oldKey); settled--; }
  });
  return result;
}

export const TOOL_NAMES = TOOL_DEFINITIONS.map(tool => tool.name);
